import React, { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import {
  FaHistory,
  FaInfoCircle,
  FaArrowDown,
  FaArrowUp,
  FaWallet,
  FaExchangeAlt,
  FaCreditCard,
  FaRegCalendarAlt,
} from "react-icons/fa";
import "./ClientDashboard.css";
import { db } from "../firebase";
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";

export default function ClientDashboard() {
  const [balance, setBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [status, setStatus] = useState("Unknown");
  const [fullName, setFullName] = useState("Client");
  const userId = localStorage.getItem("currentUser");

  // Deposit types
  const depositTypes = new Set(["Deposit", "Bonus", "Fee Refund", "New Investment"]);

  const formatCurrency = (num) =>
    num.toLocaleString("en-US", { style: "currency", currency: "USD" });

  useEffect(() => {
    if (!userId) return;

    async function fetchUserData() {
      const userDocRef = doc(db, "users", userId);
      const userDocSnap = await getDoc(userDocRef);

      if (userDocSnap.exists()) {
        const data = userDocSnap.data();
        setFullName(data.fullName || data.username || "Client");

        const startingBalance = data.startingBalance || 0;
        setStatus(data.status || "Unknown");

        const withdrawalsQuery = query(
          collection(db, "users", userId, "withdrawals"),
          orderBy("timestamp", "desc")
        );

        const unsubscribe = onSnapshot(withdrawalsQuery, (querySnapshot) => {
          const txns = [];
          let totalDeposits = 0;
          let totalWithdrawals = 0;

          querySnapshot.forEach((doc) => {
            const data = doc.data();
            const type = data.type || "Withdrawal";

            txns.push({
              id: doc.id,
              amount: data.amount,
              type,
              accountNumber: data.accountNumber,
              routingNumber: data.routingNumber,
              timestamp: data.timestamp ? data.timestamp.toDate() : new Date(),
              status: data.status || "Pending",
              description:
                data.description ||
                (depositTypes.has(type) ? "Deposit" : "Withdrawal"),
            });

            if (depositTypes.has(type)) {
              totalDeposits += data.amount;
            } else {
              totalWithdrawals += data.amount;
            }
          });

          setTransactions(txns);
          setBalance(startingBalance + totalDeposits - totalWithdrawals);
        });

        return unsubscribe;
      } else {
        setBalance(0);
        setTransactions([]);
        setStatus("Unknown");
        setFullName("Client");
      }
    }

    fetchUserData();
  }, [userId]);

  return (
    <div className="dashboard-container">
      <Navbar />
      <main className="dashboard-main">
        <header className="dashboard-header">
          <h1>Welcome {fullName}</h1>
        </header>

        <section className="summary-cards" style={{ gap: "1rem" }}>
          <div
            className="summary-card balance-card"
            style={{ padding: "1rem 1.5rem", minHeight: "120px" }}
          >
            <div
              className="card-title"
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "0.95rem",
              }}
            >
              <span>Available Balance</span>
              <FaWallet />
            </div>
            <h3
              className="card-value"
              style={{ fontSize: "1.6rem", margin: "0.4rem 0" }}
            >
              {formatCurrency(balance)}
            </h3>
            <p className="card-subtext">Ready for withdrawal</p>
          </div>

          <div
            className="summary-card"
            style={{ padding: "1rem 1.5rem", minHeight: "120px" }}
          >
            <div
              className="card-title"
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "0.95rem",
              }}
            >
              <span>Total Transactions</span>
              <FaExchangeAlt />
            </div>
            <h3
              className="card-value"
              style={{ fontSize: "1.6rem", margin: "0.4rem 0" }}
            >
              {transactions.length}
            </h3>
            <p className="card-subtext">Latest activity shown below</p>
          </div>

          <div
            className="summary-card"
            style={{ padding: "1rem 1.5rem", minHeight: "120px" }}
          >
            <div
              className="card-title"
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "0.95rem",
              }}
            >
              <span>My Card</span>
              <FaCreditCard />
            </div>
            <h3
              className="card-value"
              style={{
                color: status === "Blocked" ? "#ef4444" : "#000",
                fontSize: "1.6rem",
                margin: "0.4rem 0",
              }}
            >
              {status}
            </h3>
            <p className="card-subtext">Status of your linked card</p>
            <a href="/my-card" className="card-link">
              View Card
            </a>
          </div>
        </section>

        <section className="recent-activity">
          <h3>
            <FaHistory className="icon" />
            Recent Activity
          </h3>
          <p className="subtitle">A quick look at your latest transactions.</p>
          <ul className="transactions-list">
            {transactions
              .slice(0, 3)
              .map(({ id, description, timestamp, status, amount, type }) => {
                const statusClass =
                  status === "Completed"
                    ? "status-completed"
                    : status === "Pending"
                    ? "status-pending"
                    : "status-failed";

                const isDeposit = depositTypes.has(type);

                return (
                  <li key={id} className="transaction-item">
                    <div className="transaction-left">
                      <div className="transaction-icon">
                        {isDeposit ? (
                          <FaArrowDown color="#38b000" />
                        ) : (
                          <FaArrowUp color="#ef4444" />
                        )}
                      </div>
                      <div className="transaction-desc">
                        <p>{description}</p>
                        <p className="transaction-date">
                          {timestamp?.toLocaleString() || "Unknown date"}
                        </p>
                      </div>
                    </div>
                    <div className="transaction-right">
                      <span className={`status-badge ${statusClass}`}>
                        {status}
                      </span>
                      <span
                        className={isDeposit ? "amount-positive" : "amount-negative"}
                      >
                        {isDeposit ? "+" : "-"}
                        {formatCurrency(amount)}
                      </span>
                      <FaInfoCircle className="info-icon" title="More info" />
                    </div>
                  </li>
                );
              })}
          </ul>
          <div className="view-all">
            <a href="/history">View All Transactions →</a>
          </div>
        </section>

        <section className="earning-potential" style={{ marginTop: "2rem" }}>
          <div
            style={{
              backgroundColor: "#f1f5f9",
              borderRadius: "12px",
              overflow: "hidden",
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
            }}
          >
            <div
              style={{
                backgroundColor: "#f8fafc",
                padding: "1rem 1.5rem",
                borderBottom: "1px solid #e2e8f0",
              }}
            >
              <h3>
                <FaArrowUp style={{ color: "#1e1eaa" }} /> Earning Potential
              </h3>
              <p style={{ fontSize: "0.9rem", color: "#1a202c", margin: 0 }}>
                Projected interest earnings based on your current balance.
              </p>
            </div>

            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "space-between",
                gap: "1rem",
                padding: "1.5rem",
                backgroundColor: "#ffffff",
              }}
            >
              {[
                { label: "Daily", multiplier: 0.0018, color: "#f59e0b" },
                { label: "Weekly", multiplier: 0.0018 * 7, color: "#f59e0b" },
                { label: "Monthly", multiplier: 0.0018 * 30, color: "#f59e0b" },
              ].map(({ label, multiplier, color }) => (
                <div
                  key={label}
                  style={{
                    flex: 1,
                    backgroundColor: "#f9fafb",
                    padding: "1.25rem",
                    borderRadius: "10px",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                    textAlign: "left",
                  }}
                >
                  <p
                    style={{
                      fontSize: "0.9rem",
                      color,
                      fontWeight: "600",
                      marginBottom: "0.5rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.4rem",
                    }}
                  >
                    <FaRegCalendarAlt />
                    {label}
                  </p>
                  <h4
                    style={{
                      fontSize: "1.35rem",
                      color: "#22c55e",
                      fontWeight: "600",
                      margin: 0,
                    }}
                  >
                    {formatCurrency(balance * multiplier)}
                  </h4>
                </div>
              ))}
            </div>

            <div
              style={{
                backgroundColor: "#ffffff",
                padding: "1rem 1.5rem",
                borderTop: "1px solid #e2e8f0",
                fontSize: "0.85rem",
                color: "#718096",
                textAlign: "center",
              }}
            >
              Your earnings are calculated at 0.18% daily interest and added to
              your balance daily.
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
